"use client";

// ЛЁГКАЯ таблица-блок — второй стандарт таблиц портала.
//
// ЗАЧЕМ ОТДЕЛЬНО ОТ HeatmapGrid. Тот — «полотно»: группы колонок с цветными
// шапками, перетаскивание, закрепление, сворачивание, запоминание раскладки в
// localStorage. Это правильно для экрана-таблицы («Поиск новинок», «Заказы»,
// «Себестоимость»), но избыточно для таблицы, которая живёт БЛОКОМ внутри
// карточки: 5–8 колонок, посмотрел — пошёл дальше.
//
// Такие блоки писались руками в каждом файле (стоимость найма, брак по
// поставщикам, подменные артикулы) — со своей липкой шапкой, своими итогами и
// без сортировки. Здесь это собрано один раз. Образец вида — «Стоимость найма».
//
// ⚠️ ПОЧЕМУ ЭТО GRID, А НЕ <table>. Шапка и строка итогов должны сливаться с
// панелью (фон прозрачный), и при этом строки не должны просвечивать сквозь них
// при прокрутке. Замаскировать не выходит: фон панели НЕ ОДНОТОННЫЙ (у
// .glass-panel есть радиальный градиент), поэтому любая заливка шапки заметна
// как плашка, а полупрозрачная вуаль пропускает текст.
//
// Значит строки не должны попадать под шапку физически: шапка и итоги вынесены
// ИЗ прокручиваемой области, скроллится только тело между ними. С <table> так не
// получалось — три отдельные таблицы разъезжались по колонкам, а table-layout:
// fixed в узком блоке (панель 609px при сумме колонок 650px) схлопывал первую
// колонку в ноль. Grid решает это одной строкой: у шапки, тела и итогов ОДИН
// grid-template-columns, поэтому колонки совпадают при любой ширине блока.
//
// Когда что брать:
//   • таблица занимает весь экран вкладки, много колонок → HeatmapGrid;
//   • таблица — блок внутри карточки/панели, колонок немного → DataTable;
//   • строка = показатель, колонка = период (ОПиУ, P&L) → своя вёрстка.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from "react";
import { useFillHeight } from "@/lib/use-fill-height";
import { HelpDot } from "@/components/ui/help-dot";
import { Empty, Skeleton } from "@/components/ui/states";
import { BackToTop, useEdgeFade } from "@/components/ui/scroll-extras";
import { Collapse } from "@/components/ui/collapse";
import { captureFlip, playFlip, type FlipSnapshot } from "@/lib/flip";

export type DataCol<T, Tot = void> = {
  id: string;
  label: ReactNode;
  /** Справка «?» рядом с подписью: что за число и откуда. */
  help?: string;
  /** Ширина колонки: число — пиксели; строка — любое значение грид-дорожки
   *  («2fr», «minmax(64px, 1fr)»). Без неё колонка делит остаток поровну. */
  width?: number | string;
  /** Выравнивание. По умолчанию первая колонка влево, остальные по центру. */
  align?: "left" | "center" | "right";
  /** Моноширинные цифры — выравнивание по разрядам. */
  mono?: boolean;
  /** Значение для сортировки. Без него колонка не сортируется. */
  sortBy?: (row: T) => string | number;
  /** Что дорисовать в заголовке справа от подписи: фильтр, кнопка.
   *  Клик по нему не должен менять сортировку — компонент это гасит. */
  headerExtra?: ReactNode;
  cell: (row: T) => ReactNode;
  /** Ячейка строки итогов. Без неё в итогах пусто. */
  foot?: (totals: Tot) => ReactNode;
};

type Props<T, Tot> = {
  rows: T[];
  columns: DataCol<T, Tot>[];
  getRowKey: (row: T) => string | number;
  /** Колонка, по которой таблица отсортирована при открытии. */
  defaultSortId?: string;
  defaultSortDir?: "asc" | "desc";
  /** Итоги: считаются по отображаемым строкам. Без него строки итогов нет. */
  aggregate?: (rows: T[]) => Tot;
  /** Подпись первой ячейки итогов. По умолчанию — «Итого (N)». */
  totalsLabel?: (count: number) => ReactNode;
  onRowClick?: (row: T) => void;
  /** Доп. класс строки — подсветить выбранную, пометить проблемную. */
  rowClassName?: (row: T) => string | undefined;
  /** Вид строк: `lines` — разделители-линии (по умолчанию), `cards` — каждая
   *  строка на своей подложке с зазором. Колонки и выравнивание одинаковы:
   *  карточка отличается только оформлением, а не раскладкой (Максим,
   *  31.08.2026: «карточки могут выглядеть практически как таблица»). */
  rowStyle?: "lines" | "cards";
  /** Подписи колонок: `line` — 11/500 из `.field-label` (по умолчанию),
   *  `bold` — 12/700 приглушённым, как в сводной таблице SKU (R&D → Коллекция)
   *  и в макетах заказчика. Линия под шапкой в обоих случаях одна и та же.
   *  ⚠️ СВОЕЙ ЗАЛИВКИ У ШАПКИ НЕТ. Плашка тут была и убрана 31.08.2026: в
   *  макете заказчика вся таблица — и шапка, и строки — лежит на ОДНОЙ
   *  приподнятой подложке (`.table-surface`), а шапка отделена от строк только
   *  линией. Отдельная плашка цветом `--surface` вдобавок совпадала с фоном
   *  панели и была не видна вовсе. Вертикальных линий между колонками нет и
   *  заголовки не центрируются — оба приёма образца Максим отклонил.
   *  Строки-карточки (`rowStyle="cards"`) включают `bold` сами: над карточками
   *  подпись должна звучать твёрже. */
  headStyle?: "line" | "bold";
  /** Разворот строки: что показать под ней. Если задан, в первой колонке
   *  появляется шеврон, а по клику строка раскрывается.
   *  Вторым аргументом отдаётся шаблон колонок — чтобы вложенные строки можно
   *  было выложить В ТЕ ЖЕ колонки, а не «таблицей в таблице». */
  renderExpanded?: (row: T, gridTemplate: string) => ReactNode;
  /** Действия строки (№40): появляются под курсором у правого края и не
   *  сдвигают содержимое — под них зарезервирована колонка. По Tab тоже
   *  показываются, иначе с клавиатуры до них не добраться. */
  rowActions?: (row: T) => ReactNode;
  /** Ширина зарезервированной полосы под действия. */
  rowActionsWidth?: number;
  /** Идёт догрузка следующей порции (№42): внизу дорисовываются строки-заготовки
   *  по форме таблицы — прокрутка не упирается в пустоту и не дёргается. */
  loadingMore?: boolean;
  emptyTitle?: string;
  emptyText?: string;
  /** Докрутили почти до конца — можно подгружать следующую порцию.
   *  Надёжнее IntersectionObserver снаружи: прокрутка здесь ВНУТРЕННЯЯ, и
   *  наблюдатель, настроенный на окно, не срабатывает. */
  onEndReached?: () => void;
  /** Что дорисовать ВНУТРИ прокручиваемой области после строк: маркер
   *  бесконечной подгрузки, подпись «загружаю ещё». Снаружи он не сработал бы —
   *  наблюдатель следит за прокруткой тела, а не страницы. */
  afterRows?: ReactNode;
  /** Высота прокручиваемого ТЕЛА. По умолчанию считается от реального положения
   *  блока на экране (useFillHeight) — фиксированная формула вроде
   *  «100dvh − 320px» не годится: блок стоит на разной высоте на разных вкладках,
   *  и таблица либо вылезала за экран, либо оставляла пустоту. */
  maxHeight?: string;
  className?: string;
};

const ALIGN = { left: "text-left", center: "text-center", right: "text-right" } as const;
const LINE = "color-mix(in srgb, var(--border) 55%, transparent)";
/** Границы шапки и строки итогов — толще и контрастнее внутренних линий: это
 *  края таблицы, а не разделители строк. Тонкая линия в 55% прозрачности
 *  терялась среди них, и шапка «сливалась» с первой строкой данных. */
// ⚠️ Линия под шапкой и над итогами: была 2px от 50 % --textMuted и читалась
// как жирная черта поперёк таблицы. У заказчика границы едва заметны — берём
// цвет рамки и половинную плотность (Максим, 31.08.2026: «посмотри, как мягко
// это сделано у них», «нет прям сильно явной рамки»).
const EDGE_LINE = "color-mix(in srgb, var(--border) 70%, transparent)";
/** Вертикальный разделитель колонок — слева у всех ячеек, кроме первой. */

export function DataTable<T, Tot = void>({
  rows,
  columns,
  getRowKey,
  defaultSortId,
  defaultSortDir = "desc",
  aggregate,
  totalsLabel,
  onRowClick,
  rowClassName,
  rowStyle = "lines",
  headStyle = "line",
  renderExpanded,
  rowActions,
  rowActionsWidth = 92,
  loadingMore,
  afterRows,
  onEndReached,
  emptyTitle = "Пока пусто",
  emptyText,
  maxHeight,
  className,
}: Props<T, Tot>) {
  const [sort, setSort] = useState<{ id: string; dir: "asc" | "desc" } | null>(
    defaultSortId ? { id: defaultSortId, dir: defaultSortDir } : null,
  );

  // Высота тела — до низа видимой области, если не задана явно.
  // ⚠️ Из доступного места надо вычесть то, что стоит НИЖЕ тела: строку итогов и
  // нижний отступ панели. Иначе «Итого» уезжает за край экрана — тело занимает
  // всю высоту, а итоги оказываются уже под ней.
  const [footH, setFootH] = useState(0);
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const footRef = useRef<HTMLDivElement | null>(null);
  const headWrapRef = useRef<HTMLDivElement | null>(null);
  const footWrapRef = useRef<HTMLDivElement | null>(null);

  /** Горизонтальная прокрутка тела тянет за собой шапку и итоги — иначе на
   *  широкой таблице колонки разъезжаются с заголовками. */
  const syncScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (headWrapRef.current) headWrapRef.current.scrollLeft = el.scrollLeft;
    if (footWrapRef.current) footWrapRef.current.scrollLeft = el.scrollLeft;
    if (onEndReached && el.scrollTop + el.clientHeight >= el.scrollHeight - 300) onEndReached();
  }, [onEndReached]);
  const { ref: fillRef, maxHeight: autoHeight, remeasure } = useFillHeight<HTMLDivElement>(
    footH + 28,
    200,
  );

  // ⚠️ Полоса прокрутки тела съедает его ширину, а шапка и итоги живут ВНЕ
  // прокрутки и остаются шире — из-за этого вертикальная сетка «плыла»:
  // линии шапки не совпадали с линиями строк. Меряем полосу и компенсируем
  // одинаковым отступом справа.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Затухание у нижнего края тела (№13) — сразу видно, что строки продолжаются.
  const edgeFade = useEdgeFade<HTMLDivElement>();
  const [scrollbar, setScrollbar] = useState(0);
  const measureScrollbar = useCallback(() => {
    const el = bodyRef.current;
    if (el) setScrollbar(el.offsetWidth - el.clientWidth);
  }, []);
  useEffect(() => {
    measureScrollbar();
    edgeFade.update();
    window.addEventListener("resize", measureScrollbar);
    return () => window.removeEventListener("resize", measureScrollbar);
  }, [measureScrollbar, edgeFade, rows.length]);

  // ⚠️ ref тела — СТАБИЛЬНЫЙ колбэк. Инлайновая функция здесь пересоздаётся на
  // каждый рендер, React отцепляет и прицепляет ref заново, замер высоты внутри
  // вызывает setState — и это зацикливается («Maximum update depth exceeded»).
  const setBodyRef = useCallback((el: HTMLDivElement | null) => {
    bodyRef.current = el;
    fillRef(el);
    edgeFade.setRef(el);
    measureScrollbar();
  }, [fillRef, measureScrollbar, edgeFade]);

  // ⚠️ Высоту итогов меряем через ResizeObserver, а НЕ в ref-колбэке: колбэк
  // вызывается на каждый рендер, setState внутри него зацикливал обновления
  // («Maximum update depth exceeded»).
  useEffect(() => {
    const el = footRef.current;
    if (!el) return;
    const apply = () => {
      const h = el.getBoundingClientRect().height;
      setFootH((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aggregate, columns.length]);

  // Изменилась высота итогов — пересчитываем доступное место под телом.
  useEffect(() => {
    remeasure();
  }, [footH, remeasure]);

  const sorted = useMemo(() => {
    const col = sort && columns.find((c) => c.id === sort.id);
    if (!col?.sortBy) return rows;
    const get = col.sortBy;
    const k = sort!.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      if (typeof x === "number" && typeof y === "number") return (x - y) * k;
      return String(x).localeCompare(String(y), "ru") * k;
    });
  }, [rows, columns, sort]);

  const totals = useMemo(() => (aggregate ? aggregate(sorted) : undefined), [aggregate, sorted]);

  // ── Переезд строк при сортировке (№41) ────────────────────────────────────
  // Строки не подменяются мгновенно, а едут на новые места: видно, что список
  // тот же, просто в другом порядке. Сам приём FLIP и его ограничения (не
  // длиннее 200 строк, только видимое окно) — в lib/flip.ts, он же обслуживает
  // расступающиеся строки при перетаскивании (№33).
  const rowEls = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const flipFrom = useRef<FlipSnapshot | null>(null);

  useLayoutEffect(() => {
    const snapshot = flipFrom.current;
    flipFrom.current = null;
    playFlip(snapshot, bodyRef.current);
  }, [sort, rows.length]);

  function toggleSort(col: DataCol<T, Tot>) {
    if (!col.sortBy) return;
    flipFrom.current = captureFlip(rowEls.current.values());
    setSort((s) =>
      s?.id === col.id
        ? { id: col.id, dir: s.dir === "asc" ? "desc" : "asc" }
        : { id: col.id, dir: "desc" },
    );
  }

  if (rows.length === 0) {
    return <Empty compact title={emptyTitle} text={emptyText} className={className} />;
  }

  // ОДИН шаблон колонок на шапку, тело и итоги — этим они и выравниваются.
  // Колонка без width делит остаток: minmax(0, 1fr) не даёт длинному тексту
  // распирать колонку (обычный 1fr считается от min-content).
  // Полосу под действия строки резервируем ОТДЕЛЬНОЙ КОЛОНКОЙ, а не отступом
  // справа: отступ пришлось бы повторить в шапке и итогах, иначе вертикальная
  // сетка разъезжается. Колонка есть у всех трёх — выравнивание держится само.
  const template = [
    ...columns.map((c) =>
      typeof c.width === "number" ? `${c.width}px` : (c.width ?? "minmax(0, 1fr)"),
    ),
    ...(rowActions ? [`${rowActionsWidth}px`] : []),
  ].join(" ");

  // Минимальная ширина всей таблицы: сумма минимумов колонок. Если она больше
  // блока — включается горизонтальная прокрутка, и шапка с итогами едут вместе
  // с телом (см. syncScroll ниже).
  const minTotal = columns.reduce((sum, c) => {
    if (typeof c.width === "number") return sum + c.width;
    const m = typeof c.width === "string" ? /minmax\(\s*(\d+)px/.exec(c.width) : null;
    return sum + (m ? Number(m[1]) : 120);
  }, 0) + (rowActions ? rowActionsWidth : 0);

  const alignOf = (c: DataCol<T, Tot>, i: number) => ALIGN[c.align ?? (i === 0 ? "left" : "center")];
  /** Отступы ячейки. У ПЕРВОЙ колонки левый отступ убран — её содержимое стоит
   *  вровень с краем таблицы. Но если такая колонка ЦЕНТРИРОВАНА (узкая колонка
   *  флажков), центр считается внутри урезанной области и содержимое уезжает
   *  влево на половину правого отступа: флажки стояли не по центру своей ячейки
   *  (Максим, 27.08.2026). Центрированной колонке отступы симметричные. */
  // ⚠️ В обычной таблице первая колонка ЛИПНЕТ к левому краю намеренно: так
  // содержимое выравнивается с заголовком экрана. В карточном виде у строки есть
  // подложка, и то же правило прижимает имя к её кромке (Максим, 31.08.2026:
  // «слишком близко к краю, некрасиво») — там первой колонке нужен отступ.
  // padOf используется и шапкой, и строками, и итогами, поэтому колонки
  // остаются выровненными между собой.
  const padOf = (i: number, c?: DataCol<T, Tot>) => {
    // ⚠️ ОТСТУП ДАЁТ ЯЧЕЙКА, А НЕ КОНТЕЙНЕР. Таблица прилегает к краям блока,
    // чтобы подсветка строки шла во всю ширину, — значит воздух у крайних
    // колонок нужен свой, иначе аватар с именем липнет к кромке (Максим,
    // 31.08.2026: «данные очень прижаты с левого края»).
    const first = i === 0 && (c?.align ?? "left") === "left";
    const last = i === columns.length - 1;
    if (first) return last ? "px-4" : "pl-4 pr-3";
    return last ? "pl-3 pr-4" : "px-3";
  };

  const boldHead = headStyle === "bold" || rowStyle === "cards";

  return (
    <div className={`relative flex flex-col ${className ?? ""}`}>
      {/* ── Шапка: вне вертикальной прокрутки; по горизонтали едет за телом ── */}
      <div ref={headWrapRef} className="overflow-hidden" style={{ paddingRight: scrollbar }}>
      <div
        // Кегль и вес подписей сняты замером с образца (R&D → Коллекция и макеты
        // заказчика): 12/700 приглушённым, полоса высотой 30px. Фона и рамки у
        // шапки нет — она лежит на подложке всей таблицы (см. `.table-surface`).
        className={`grid ${
          boldHead ? "text-[12px] font-bold text-[var(--textMuted)]" : "field-label"
        } ${rowStyle === "cards" ? "mb-2" : ""}`}
        style={{
          gridTemplateColumns: template,
          boxShadow: `inset 0 -1px 0 ${EDGE_LINE}`,
          minWidth: minTotal,
        }}
      >
        {columns.map((c, i) => {
          const active = sort?.id === c.id;
          return (
            <div
              key={c.id}
              onClick={() => toggleSort(c)}
              
              // Вес шапки берётся из .field-label (600, как у подписей интерфейса):
              // локальный font-semibold перебивал его на 500 и делал шапку легче строк.
              className={`min-w-0 break-words ${boldHead ? "py-1.5" : "py-2"} ${alignOf(c, i)} ${padOf(i, c)} ${
                c.sortBy ? "cursor-pointer select-none transition hover:text-[var(--text)]" : ""
              }`}
            >
              <span className={`inline-flex items-center gap-1 ${active ? "text-[var(--primary)]" : ""}`}>
                {c.label}
                {c.sortBy && (
                  // Стрелка не подменяется другим значком, а ПОВОРАЧИВАЕТСЯ:
                  // видно, что это тот же указатель сменил направление. У
                  // неактивной колонки он бледный и смотрит вниз — намёк, что
                  // по ней тоже можно отсортировать.
                  <span
                    aria-hidden
                    className="sort-arrow inline-block"
                    data-active={active ? "1" : "0"}
                    style={{ transform: active && sort!.dir === "asc" ? "rotate(180deg)" : undefined }}
                  >
                    ↓
                  </span>
                )}
                {c.help && <HelpDot text={c.help} size={11} />}
              </span>
            </div>
          );
        })}
        {rowActions && <div aria-hidden />}
      </div>
      </div>

      {/* ── Тело: единственная прокручиваемая часть. Строки обрезаются здесь и
             под шапку/итоги не попадают. ── */}
      <div
        ref={setBodyRef}
        className="min-h-0 overflow-auto scrollbar-autohide"
        onScroll={syncScroll}
        // overscrollBehavior: contain — дойдя до конца таблицы, колесо больше не
        // прокручивает страницу под ней.
        style={{ maxHeight: maxHeight ?? (autoHeight ? `${autoHeight}px` : undefined), overscrollBehavior: "contain" }}
        onTransitionEnd={remeasure}
      >
        <div style={{ minWidth: minTotal }}>
        {sorted.map((row) => {
          const key = getRowKey(row);
          const open = expanded.has(key);
          const toggle = () =>
            setExpanded((prev) => {
              const next = new Set(prev);
              next.has(key) ? next.delete(key) : next.add(key);
              return next;
            });
          return (
            <div key={key}>
              <div
                ref={(el) => {
                  if (el) rowEls.current.set(key, el);
                  else rowEls.current.delete(key);
                }}
                onClick={renderExpanded ? toggle : onRowClick ? () => onRowClick(row) : undefined}
                // items-center: ячейки строки выравниваются по центру ПО ВЫСОТЕ.
                // Без него grid растягивает каждую ячейку на всю высоту строки, и
                // короткое содержимое липнет к верхней кромке: в строке с именем,
                // почтой и телефоном флажок стоял на 6 px от верха и 22 от низа
                // (Максим, 27.08.2026).
                className={`row-actions-host grid items-center text-[12px] ${
                  rowStyle === "cards"
                    ? "mb-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]"
                    : "border-b border-[color-mix(in_srgb,var(--border)_55%,transparent)]"
                } ${
                  // ⚠️ Подсветка была на 40 % прозрачности и в тёмной теме почти не
                  // читалась — ловилась только замером (31.08.2026). Теперь полный тон.
                  onRowClick || renderExpanded ? "cursor-pointer transition hover:bg-[var(--hover)]" : ""
                } ${rowClassName?.(row) ?? ""}`}
                style={{ gridTemplateColumns: template }}
              >
                {columns.map((c, i) => (
                  <div
                    key={c.id}
                    
                    // ⚠️ ДАННЫЕ — ОСНОВНЫМ ЦВЕТОМ, ПРИГЛУШЕНЫ ТОЛЬКО ЗАГОЛОВКИ
                    // (Максим, 31.08.2026: «у нас всё одного цвета, и это не
                    // выделяет данные»). Раньше приглушались ВСЕ колонки кроме
                    // первой — и содержимое строки оказывалось того же тона, что
                    // подписи колонок: глазу не за что зацепиться. В образце
                    // заказчика ровно наоборот — шапка тихая, данные яркие.
                    // ⚠️ ПЕРВАЯ КОЛОНКА НАЗЫВАЕТ ЗАПИСЬ — вес 500 (docs/typography.md).
                    // В макетах заказчика имя человека и название товара плотнее
                    // соседних данных, но не дотягивают до заголовков: там 600.
                    className={`min-w-0 py-2 leading-tight text-[var(--text)] ${i === 0 ? "font-medium" : ""} ${padOf(i, c)} ${alignOf(c, i)} ${
                      (c.mono ?? i > 0) ? "font-mono" : ""
                    }`}
                  >
                    {i === 0 && renderExpanded ? (
                      <span className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className="inline-block w-3 shrink-0 text-[var(--textMuted)] transition-transform"
                          style={{ transform: open ? "rotate(90deg)" : undefined }}
                        >
                          ›
                        </span>
                        <span className="min-w-0 flex-1">{c.cell(row)}</span>
                      </span>
                    ) : (
                      c.cell(row)
                    )}
                  </div>
                ))}
                {rowActions && (
                  // Клик по действию не должен раскрывать строку или открывать
                  // карточку — гасим всплытие прямо здесь, чтобы каждый
                  // вызывающий не помнил об этом сам.
                  <div
                    className="row-actions flex items-center justify-end gap-1 pr-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {rowActions(row)}
                  </div>
                )}
              </div>
              {renderExpanded ? (
                // №22: вложенные строки раскрываются высотой, а не прыжком —
                // то, что под ними, не скачет, и взгляд не теряет строку.
                <Collapse open={open}>
                  <div className="border-b border-[var(--border)]">
                    {renderExpanded(row, template)}
                  </div>
                </Collapse>
              ) : null}
            </div>
          );
        })}
        {loadingMore && (
          <div aria-hidden style={{ minWidth: minTotal }}>
            {[0, 1].map((i) => (
              <div
                key={i}
                className="grid border-b border-[var(--border)]"
                style={{ gridTemplateColumns: template, opacity: 1 - i * 0.35 }}
              >
                {columns.map((c, ci) => (
                  <div key={c.id} className={`min-w-0 py-2 ${padOf(ci, columns[ci])}`}>
                    <Skeleton height={13} width={ci === 0 ? "70%" : "45%"} />
                  </div>
                ))}
                {rowActions && <div />}
              </div>
            ))}
          </div>
        )}
        </div>
        {afterRows}
      </div>

      {/* «Наверх» (№37): всплывает после третьего экрана прокрутки тела. */}
      <BackToTop targetRef={bodyRef} />

      {/* ── Итоги: вне прокрутки ── */}
      {totals !== undefined && (
        <div ref={footWrapRef} className="overflow-hidden" style={{ paddingRight: scrollbar }}>
        <div
          ref={footRef}
          className="grid text-[12px] font-semibold"
          style={{ gridTemplateColumns: template, boxShadow: `inset 0 1px 0 ${EDGE_LINE}`, minWidth: minTotal }}
        >
          {columns.map((c, i) => (
            <div
              key={c.id}
              
              className={`min-w-0 py-2 ${padOf(i, c)} ${alignOf(c, i)} ${
                i === 0 ? "text-[var(--text)]" : "font-mono"
              }`}
            >
              {i === 0
                ? (totalsLabel ?? ((n: number) => `Итого (${n})`))(sorted.length)
                : c.foot?.(totals as Tot)}
            </div>
          ))}
          {rowActions && <div aria-hidden />}
        </div>
        </div>
      )}
    </div>
  );
}
